import Component from "../../lib/Component.js";
import { onTransitionEnd } from "../helpers.js";

/**
 * @typedef {{
 *  scrollBody: boolean,
 *  closeOnBackdropClick: boolean,
 *  componentName: string|undefined,
 *  onOpen: () => Promise<void>|undefined,
 *  onOpenFinished: () => Promise<void>|undefined,
 *  onClose: () => Promise<void>|undefined,
 *  onCloseFinished: () => Promise<void>|undefined
 *  onMount: () => void|undefined,
 *  onUnmount: () => void|undefined
 * }} BottomSheetOptions
 */

/**
 * @typedef {{
 *  el: HTMLElement|null,
 *  open: () => Promise<void>,
 *  close: () => Promise<void>,
 *  setHeader: (headerText: string|HTMLElement) => void,
 *  setContent: (content: string|HTMLElement) => void,
 * } & Component} BottomSheetComponent
 */

const bottomSheets = new Map();

/**
 *
 * @param {string} id
 * @param {{header?: boolean} & BottomSheetOptions} options
 * @returns {BottomSheetComponent}
 */
export const createBottomSheet = (id, options) => {
  const { header = false, ...sheetOptions } = options;
  let element;
  if (id && bottomSheets.has(id)) {
    const existingSheet = bottomSheets.get(id);
    element = existingSheet.el;
    existingSheet.unmount();
  } else {
    element = document.createElement("div");
    element.id = id;
    element.classList.add("bottom-sheet-backdrop");
    element.innerHTML = `
      <div class="bottom-sheet-container">
        ${
          header
            ? `<div class="bottom-sheet-header">
          <strong></strong>
          <button class="close-bottom-sheet"><i class="fas fa-times"></i></button>
        </div>`
            : ""
        }
        <div class="bottom-sheet-body"></div>
      </div>
    `;
    document.body.appendChild(element);
  }
  const newSheet = BottomSheet(element, sheetOptions);
  bottomSheets.set(id, newSheet);
  return newSheet;
};

/**
 * Creates a bottom sheet component
 * @param {HTMLElement} element - The bottom sheet element
 * @param {BottomSheetOptions} options - Configuration options
 * @returns {BottomSheetComponent}
 */
const BottomSheet = (
  element,
  {
    scrollBody = false,
    closeOnBackdropClick = true,
    componentName,
    onOpen,
    onOpenFinished,
    onClose,
    onCloseFinished,
    onMount,
    onUnmount,
  } = {}
) => {
  const CLOSE_BUTTON_SELECTOR = ".close-bottom-sheet";
  const CONTAINER_SELECTOR = ".bottom-sheet-container";
  const TRANSITION_PROPERTY = "opacity";
  const TRANSITION_DURATION = 300; // .3s

  const container = element?.querySelector(CONTAINER_SELECTOR);

  const backdropClickHandler = (event) => {
    // Close on backdrop click (clicking outside the container)
    if (closeOnBackdropClick && event.target === element) {
      component.close();
    }
    // Close on close button click
    if (event.target.closest(CLOSE_BUTTON_SELECTOR)) {
      component.close();
    }
  };

  /** @type {BottomSheetComponent} */
  const component = element
    ? new Component({
        componentName: componentName || "BottomSheet",
        onMount: () => {
          if (scrollBody && container) {
            container.classList.add("scroll");
          }
          element.addEventListener("click", backdropClickHandler);
          if (onMount) onMount();
        },
        onUnmount: () => {
          if (scrollBody && container) {
            container.classList.remove("scroll");
          }
          element.removeEventListener("click", backdropClickHandler);
          if (onUnmount) onUnmount();
        },
      })
    : Component.noop();

  component.el = element;

  component.open = async (...args) => {
    if (!element) return;
    element.classList.add("active");

    // Use requestAnimationFrame to ensure display:block is applied before transition
    requestAnimationFrame(async () => {
      element.classList.add("open");
      if (onOpen) {
        await onOpen(...args);
      }
      await onTransitionEnd(element, TRANSITION_PROPERTY, TRANSITION_DURATION);
      if (onOpenFinished) {
        await onOpenFinished();
      }
    });
  };

  component.close = async () => {
    if (!element) return;
    element.classList.remove("open");
    if (onClose) {
      await onClose();
    }
    await onTransitionEnd(element, TRANSITION_PROPERTY, TRANSITION_DURATION);
    if (onCloseFinished) {
      await onCloseFinished();
    }
    element.classList.remove("active");
  };

  component.setHeader = (headerText) => {
    if (!element) return;
    const headerEl = element.querySelector(".bottom-sheet-header strong");
    if (headerEl) {
      if (typeof headerText === "string") {
        headerEl.innerHTML = headerText;
      } else {
        headerEl.innerHTML = "";
        headerEl.appendChild(headerText);
      }
    }
  };

  component.setContent = (content) => {
    if (!element) return;
    const bodyEl = element.querySelector(".bottom-sheet-body");
    if (bodyEl) {
      if (typeof content === "string") {
        bodyEl.innerHTML = content;
      } else {
        bodyEl.innerHTML = "";
        bodyEl.appendChild(content);
      }
    }
  };

  return component;
};

export default BottomSheet;
